import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowDown, Plane, PackageCheck } from "lucide-react";
import { HERO_IMG } from "@/data/products";

const LINES = ["FLY IN LIGHT.", "PRINT ON GROUND."];
const HUBS = ["LHR", "JFK", "BER", "SYD", "NRT"];

const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, -90]);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ rx: -py * 10, ry: px * 12 });
  };

  return (
    <section data-testid="hero-section" className="relative overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28">
      <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse 60% 50% at 70% 20%, rgba(9,9,11,0.05), transparent)" }} />
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1.2fr_1fr]">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="mb-6 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-zinc-500"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-tour-red animate-pulse-dot" />
            Global merch hub for touring bands
          </motion.p>

          <h1 className="font-display text-4xl font-extrabold leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease: EASE }}
                  className="block"
                >
                  {i === 1 ? <span className="font-serif italic font-medium">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55, ease: EASE }}
            className="mt-7 max-w-xl text-base text-zinc-600 sm:text-lg"
          >
            Save thousands in international baggage fees and customs. We design your merch,
            produce it through print-on-demand partners in your destination country, and ship
            directly to your band, venue or hotel — domestic or international.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-start-enquiry-btn"
              onClick={() => navigate("/enquiry")}
              className="group flex items-center gap-2 bg-tour-void px-7 py-4 font-mono text-xs uppercase tracking-widest text-white transition-colors hover:bg-tour-red"
            >
              <Plane className="h-4 w-4 transition-transform group-hover:-translate-y-0.5" />
              Start your enquiry
            </button>
            <button
              data-testid="hero-view-catalog-btn"
              onClick={() => navigate("/catalog")}
              className="flex items-center gap-2 border border-zinc-300 px-7 py-4 font-mono text-xs uppercase tracking-widest text-zinc-700 transition-colors hover:border-black hover:text-black"
            >
              View catalog
              <ArrowDown className="h-4 w-4" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-2"
          >
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">Now routing to</span>
            {HUBS.map((h, i) => (
              <motion.span
                key={h}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 + i * 0.1, duration: 0.5, ease: EASE }}
                data-testid={`hero-hub-${h}`}
                className="font-mono text-xs tracking-widest text-zinc-800"
              >
                {h}
              </motion.span>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4, ease: EASE }}
          style={{ y }}
          className="relative hidden lg:block"
        >
          <div
            ref={ref}
            onMouseMove={onMove}
            onMouseLeave={() => setTilt({ rx: 0, ry: 0 })}
            style={{ perspective: 900 }}
            className="relative"
          >
            <motion.div
              animate={{ rotateX: tilt.rx, rotateY: tilt.ry }}
              transition={{ type: "spring", stiffness: 120, damping: 18 }}
              className="relative overflow-hidden border border-zinc-200 bg-white shadow-[0_40px_80px_-30px_rgba(9,9,11,0.25)]"
            >
              <img src={HERO_IMG} alt="Tour poster merch" className="aspect-[4/5] w-full object-cover" />
              <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.25), transparent 55%)" }} />
              <div className="absolute bottom-0 inset-x-0 flex items-center justify-between border-t border-zinc-200 bg-white/90 px-5 py-4 backdrop-blur-sm">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">MERCH-01 / TOUR POSTER</span>
                <PackageCheck className="h-4 w-4 text-tour-red" />
              </div>
            </motion.div>
            <div className="absolute -bottom-5 -left-5 -z-10 h-full w-full border border-zinc-300" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
